/**
 * RecordVideoModal — record a video clip with the user's camera and
 * send it through the existing Messenger upload pipeline.
 *
 * Why this exists: previously "Record video" wired straight to a
 * hidden `<input type="file" accept="video/*" capture="environment">`.
 * On mobile browsers that honour the `capture` attribute that opens
 * the OS camera in video mode, but on EVERY desktop browser it falls
 * back to the regular file picker — so "Record video" silently
 * behaved like "Choose video". Wayne flagged this for fix before
 * launch (iter152).
 *
 * Flow:
 *   1. Open modal → request camera + mic via getUserMedia.
 *   2. Live preview (muted local stream).
 *   3. Start recording → MediaRecorder collects chunks (60 s soft cap).
 *   4. Stop recording → preview the encoded blob via a fresh
 *      object-URL on a <video controls> element.
 *   5. Re-record (return to step 2) or Send (hand a File to the
 *      consumer prop) or Cancel (tear down).
 *
 * Mobile fallback: if MediaRecorder isn't supported, the consumer
 * should fall back to the existing capture=environment file-input
 * (handled in MessagesPage.js, not here).
 *
 * Safety: every camera/mic track + every object URL created in this
 * modal is torn down in the cleanup effect AND on every explicit
 * close path, so the camera light goes off the moment the user
 * leaves the modal. No leaks.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Video as VideoIcon, Circle, Square, RotateCcw, Send } from 'lucide-react';
import { setActiveMediaSession } from '../../api/apiClient';

const MAX_DURATION_S = 60;

// Safe diagnostic logger — never logs device labels, tokens, raw blobs
// or any user-identifying info. Only structural facts about which
// support gate triggered which UI state.
const safeLog = (event, extra = {}) => {
  // eslint-disable-next-line no-console
  console.info('[ccuk-record-video]', event, extra);
};

function checkSupport() {
  const hasNavigator = typeof navigator !== 'undefined';
  const hasGetUserMedia = !!(hasNavigator && navigator.mediaDevices?.getUserMedia);
  const hasMediaRecorder = typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined';
  // `isSecureContext` is required for getUserMedia in every modern
  // browser. The check covers http:// in particular — without this
  // we'd show "unsupported" instead of the more accurate "secure
  // context required" message.
  const isSecure = typeof window !== 'undefined' && (window.isSecureContext === true);
  const result = {
    hasGetUserMedia,
    hasMediaRecorder,
    isSecure,
    overallOk: hasGetUserMedia && hasMediaRecorder && isSecure,
  };
  safeLog('support-check', result);
  return result;
}

function isMediaRecorderSupported() {
  // Back-compat export — MessagesPage uses this to decide whether to
  // open the modal at all. We deliberately do NOT include the
  // secure-context check here so the modal still opens on insecure
  // contexts; the modal itself then shows the secure-context message.
  return (
    typeof navigator !== 'undefined'
    && navigator.mediaDevices?.getUserMedia
    && typeof window !== 'undefined'
    && typeof window.MediaRecorder !== 'undefined'
  );
}

function pickMimeType() {
  // Iter-rescue 2026-02 — fallback list per Wayne's brief. WebM first
  // (Chrome/Firefox/Edge happy path), then MP4 for Safari ≥14.1.
  // If MediaRecorder.isTypeSupported isn't a function (very old
  // browsers), we return '' and let the recorder choose its own
  // default in the createRecorder fallback below.
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
  ];
  const MR = window.MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== 'function') return '';
  for (const t of candidates) {
    try {
      if (MR.isTypeSupported(t)) {
        safeLog('selected-mime', { mime: t, source: 'preferred-list' });
        return t;
      }
    } catch { /* isTypeSupported can throw in some shells — ignore */ }
  }
  return '';
}

function createRecorder(stream) {
  // Try preferred MIME first, then fall back to default MediaRecorder
  // (no forced MIME) per spec point 7. Only after BOTH fail do we
  // surface "unsupported".
  const preferred = pickMimeType();
  if (preferred) {
    try {
      return { recorder: new MediaRecorder(stream, { mimeType: preferred }), mime: preferred };
    } catch (err) {
      safeLog('recorder-create-failed', { name: err?.name || 'Error', stage: 'preferred-mime' });
    }
  }
  try {
    const rec = new MediaRecorder(stream);
    safeLog('fallback-default-recorder', { mime: rec.mimeType || '(browser-default)' });
    return { recorder: rec, mime: rec.mimeType || '' };
  } catch (err) {
    safeLog('recorder-create-failed', { name: err?.name || 'Error', stage: 'default' });
    return { recorder: null, mime: '' };
  }
}

export default function RecordVideoModal({ open, onClose, onSend }) {
  // Phase machine — new sub-states added in iter-rescue 2026-02 so the
  // "unsupported" copy is ONLY shown when the browser genuinely can't
  // record. Permission-denied, camera-busy, and secure-context errors
  // get their own targeted messages.
  const [phase, setPhase] = useState('idle'); // 'idle'|'preview'|'recording'|'review'|'denied'|'busy'|'insecure'|'unsupported'
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sending, setSending] = useState(false);

  const liveVideoRef = useRef(null);
  const playbackVideoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef('');
  const tickRef = useRef(null);
  const objectUrlRef = useRef(null);
  // Track whether we currently hold a media-session pause so we
  // release it exactly once on close/cleanup. Without this guard,
  // re-record + close paths could double-decrement the global
  // active-session counter and accidentally resume non-essential
  // polling while another call is still live.
  const heldMediaSessionRef = useRef(false);

  // ── tear-down helpers ────────────────────────────────────────────
  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch { /* ignore */ }
      });
      streamRef.current = null;
    }
  }, []);

  const stopTicker = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      try { URL.revokeObjectURL(objectUrlRef.current); } catch { /* ignore */ }
      objectUrlRef.current = null;
    }
  }, []);

  const fullTeardown = useCallback(() => {
    stopTicker();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    recorderRef.current = null;
    stopTracks();
    revokeObjectUrl();
    chunksRef.current = [];
    setElapsed(0);
    setPreviewUrl(null);
    setPhase('idle');
    // Release the global active-media-session pause exactly once,
    // even if fullTeardown runs multiple times (StrictMode dev,
    // close → unmount race, etc.).
    if (heldMediaSessionRef.current) {
      heldMediaSessionRef.current = false;
      setActiveMediaSession(false);
    }
    safeLog('cleanup-complete');
  }, [revokeObjectUrl, stopTicker, stopTracks]);

  // ── open camera when modal opens ─────────────────────────────────
  // We deliberately do NOT touch `liveVideoRef.current` inside this
  // effect because the <video> element is rendered conditionally on
  // `phase` — when this effect runs, `phase` is still 'idle' and the
  // <video> element hasn't mounted yet. The dedicated attach effect
  // below runs after the next render and reliably wires `srcObject`
  // + calls `play()`. This was the root cause of the iter152 blank-
  // preview bug Wayne reported.
  useEffect(() => {
    if (!open) return;
    const support = checkSupport();
    if (!support.isSecure) {
      setPhase('insecure');
      return;
    }
    if (!support.hasGetUserMedia || !support.hasMediaRecorder) {
      setPhase('unsupported');
      return;
    }
    // Take the active-media-session pause while the modal is alive so
    // non-essential polling stands down (matches Wayne's spec point B12).
    if (!heldMediaSessionRef.current) {
      heldMediaSessionRef.current = true;
      setActiveMediaSession(true);
    }
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        setPhase('preview');
      } catch (err) {
        const name = err?.name || 'Error';
        safeLog('get-user-media-failed', { name });
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
          setPhase('denied');
        } else if (name === 'NotReadableError' || name === 'TrackStartError') {
          // Camera/mic is held by another tab/app/PWA. This is NOT an
          // unsupported-browser problem — show a busy-specific
          // message so the user knows to close the other consumer.
          setPhase('busy');
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          toast.error('No camera was found on this device.');
          setPhase('unsupported');
        } else {
          toast.error('Could not access the camera. Please try again.');
          setPhase('unsupported');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // ── attach the live MediaStream to the <video> AFTER it mounts.
  // Runs whenever the phase changes so the stream is re-attached when
  // the user toggles between preview ↔ recording ↔ review ↔ re-record.
  // Safari/iOS require an explicit `play()` call after `srcObject`
  // assignment because the element is muted on mount and the gesture
  // came from the modal-open click, not directly from <video>.
  useEffect(() => {
    if (phase !== 'preview' && phase !== 'recording') return;
    const v = liveVideoRef.current;
    const s = streamRef.current;
    if (!v || !s) return;
    if (v.srcObject !== s) {
      v.srcObject = s;
    }
    // play() can reject on iOS if the user backgrounded the tab —
    // swallow the rejection so we don't spam a toast for a benign race.
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => { /* ignore */ });
  }, [phase]);

  // ── close → cleanup ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) fullTeardown();
    return () => fullTeardown();
  }, [open, fullTeardown]);

  // ── start recording ──────────────────────────────────────────────
  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const { recorder: rec, mime } = createRecorder(streamRef.current);
    if (!rec) {
      toast.error('Your browser refused to start the video recorder. Try the latest Chrome, Safari or Firefox.');
      setPhase('unsupported');
      return;
    }
    mimeRef.current = mime;
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      const type = mimeRef.current || (chunksRef.current[0]?.type ?? 'video/webm');
      const blob = new Blob(chunksRef.current, { type });
      revokeObjectUrl();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setPreviewUrl(url);
      setPhase('review');
      safeLog('recording-stopped', { mime: type, size_bytes: blob.size });
    };
    rec.start(250); // collect data every 250 ms so we have something to send even on early stop
    recorderRef.current = rec;
    setElapsed(0);
    setPhase('recording');
    safeLog('recording-started', { mime: mime || '(browser-default)' });
    const startedAt = Date.now();
    tickRef.current = setInterval(() => {
      const sec = Math.floor((Date.now() - startedAt) / 1000);
      setElapsed(sec);
      if (sec >= MAX_DURATION_S) stopRecording();
    }, 250);
  };

  const stopRecording = () => {
    stopTicker();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
  };

  const reRecord = () => {
    revokeObjectUrl();
    chunksRef.current = [];
    setElapsed(0);
    setPreviewUrl(null);
    setPhase('preview');
  };

  const handleSend = async () => {
    if (sending) return;
    const type = mimeRef.current || (chunksRef.current[0]?.type ?? 'video/webm');
    const blob = new Blob(chunksRef.current, { type });
    if (!blob || blob.size === 0) {
      toast.error('Nothing was recorded — try again.');
      return;
    }
    const ext = type.includes('mp4') ? 'mp4' : 'webm';
    const file = new File(
      [blob],
      `video-message-${new Date().toISOString().replace(/[:.]/g, '-')}.${ext}`,
      { type },
    );
    setSending(true);
    try {
      await onSend?.(file);
      // Parent closes the modal on successful send.
    } catch {
      // Parent surfaces a toast — keep the recording so the user can retry.
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    fullTeardown();
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 p-3"
      role="dialog"
      data-ccuk-mobile-dialog-root="true"
      aria-modal="true"
      aria-labelledby="record-video-title"
      data-testid="record-video-modal"
    >
      <div className="w-full max-w-md rounded-2xl bg-theme-surface border border-theme/60 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme/60">
          <div className="flex items-center gap-2 text-theme-text">
            <VideoIcon className="h-4 w-4 text-[#002FA7]" />
            <h2 id="record-video-title" className="text-sm font-semibold">Record video</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="h-8 w-8 rounded-full flex items-center justify-center text-theme-muted hover:bg-theme-bg-soft"
            aria-label="Close"
            data-testid="record-video-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="aspect-[3/4] sm:aspect-video w-full bg-black flex items-center justify-center relative">
          {phase === 'denied' && (
            <div className="text-center px-6 text-white" data-testid="record-video-denied">
              <p className="text-sm">
                Camera and microphone access is blocked. Allow access in your
                browser settings, then reopen Record video.
              </p>
            </div>
          )}
          {phase === 'busy' && (
            <div className="text-center px-6 text-white" data-testid="record-video-busy">
              <p className="text-sm">
                Your camera or microphone is being used by another tab, app, or
                an active call. Close the other camera tab or end the active
                call, then reopen <strong>Record video</strong>.
              </p>
            </div>
          )}
          {phase === 'insecure' && (
            <div className="text-center px-6 text-white" data-testid="record-video-insecure">
              <p className="text-sm">
                Recording requires a secure (HTTPS) connection. Reload the
                site over <strong>https://</strong> and try again.
              </p>
            </div>
          )}
          {phase === 'unsupported' && (
            <div className="text-center px-6 text-white" data-testid="record-video-unsupported">
              <p className="text-sm">
                Video recording isn&apos;t supported on this browser. Use the latest
                Chrome, Safari or Firefox — or use <strong>Choose video</strong>
                {' '}to upload an existing clip.
              </p>
            </div>
          )}
          {(phase === 'preview' || phase === 'recording') && (
            <video
              ref={liveVideoRef}
              autoPlay
              muted
              playsInline
              className="h-full w-full object-cover"
              data-testid="record-video-live-preview"
            />
          )}
          {phase === 'review' && previewUrl && (
            <video
              ref={playbackVideoRef}
              src={previewUrl}
              controls
              playsInline
              className="h-full w-full object-contain bg-black"
              data-testid="record-video-playback"
            />
          )}
          {phase === 'recording' && (
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 text-white text-xs font-medium px-2 py-1 rounded-full">
              <span className="inline-block h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
              <span data-testid="record-video-elapsed">{String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}</span>
              <span className="opacity-60">/ {String(Math.floor(MAX_DURATION_S / 60)).padStart(2, '0')}:{String(MAX_DURATION_S % 60).padStart(2, '0')}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-theme/60">
          {phase === 'preview' && (
            <button
              type="button"
              onClick={startRecording}
              className="ml-auto inline-flex items-center gap-2 rounded-full bg-rose-500 hover:bg-rose-600 active:scale-95 text-white text-sm font-semibold px-4 py-2 transition-all"
              data-testid="record-video-start-btn"
            >
              <Circle className="h-3.5 w-3.5 fill-white" />
              Start recording
            </button>
          )}
          {phase === 'recording' && (
            <button
              type="button"
              onClick={stopRecording}
              className="ml-auto inline-flex items-center gap-2 rounded-full bg-theme-bg-soft hover:bg-theme-bg-soft/80 text-theme-text text-sm font-semibold px-4 py-2 transition-all"
              data-testid="record-video-stop-btn"
            >
              <Square className="h-3.5 w-3.5" />
              Stop
            </button>
          )}
          {phase === 'review' && (
            <>
              <button
                type="button"
                onClick={reRecord}
                disabled={sending}
                className="inline-flex items-center gap-2 rounded-full text-theme-text text-sm font-medium px-3 py-2 hover:bg-theme-bg-soft disabled:opacity-50"
                data-testid="record-video-rerecord-btn"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Re-record
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                className="inline-flex items-center gap-2 rounded-full bg-[#002FA7] hover:bg-[#002FA7]/90 active:scale-95 text-white text-sm font-semibold px-4 py-2 disabled:opacity-60 transition-all"
                data-testid="record-video-send-btn"
              >
                <Send className="h-3.5 w-3.5" />
                {sending ? 'Sending…' : 'Send'}
              </button>
            </>
          )}
          {(phase === 'denied' || phase === 'busy' || phase === 'insecure' || phase === 'unsupported' || phase === 'idle') && (
            <button
              type="button"
              onClick={handleClose}
              className="ml-auto inline-flex items-center gap-2 rounded-full text-theme-text text-sm font-medium px-4 py-2 hover:bg-theme-bg-soft"
              data-testid="record-video-cancel-btn"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export { isMediaRecorderSupported };
